import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
} from "react";
import {
  StyleSheet,
  View,
  FlatList,
} from "react-native";

import { useIsFocused, useRoute } from "@react-navigation/native";
import { Text } from "react-native-paper";

import MiniAudioPlayer from "../../components/MiniAudioPlayer";
import AppLoader from "../../components/AppLoader";

import { ARTICLE_HEIGHT } from "../../shared/constants/dimension";
import { useArticlesData } from "../../providers/ArticlesDataProvider";
import { useTrackScreenTime } from "../../analytics/useTrackScreenTime";
import { useAudioContext } from "../../providers/AudioProvider";
import { useActiveArticle } from "../news-feed/hooks/useActiveArticle";
import { trackInteraction } from "../../analytics/analyticsUtil";
import { ACTION_TYPE, ANALYTICS_EVENT } from "../../analytics/types";
import { ROUTES } from "../../shared/constants/routes";

import { useTemplateRows } from "./hooks/useTemplateRows";
import NewsFeedList from "./components/NewsFeedList";
import CustomLinearGradient, { GRADIENT_TYPE } from "../../components/CustomLinearGradient";

const LOAD_MORE_THRESHOLD = ARTICLE_HEIGHT * 3;

export function NewsFeed({ fromRadio = false }) {
  useTrackScreenTime(fromRadio ? "HOME_RADIO" : "HOME_NEWSPAPER");

  const scrollViewRef = useRef<FlatList>(null);
  const isUserScrolling = useRef(false);
  const lastScrolledArticle = useRef<string | null>(null);
  const scrollRetryTimeout = useRef<any>(null);

  const [isRefreshing, setIsRefreshing] = useState(false);

  const isFocused = useIsFocused();
  const route = useRoute();

  const { setPlaybackFocus, isPlaying } = useAudioContext();
  const { setActiveArticleId, activeArticle } = useActiveArticle();
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);

  useEffect(() => {
    return () => {
      if (fromRadio) setPlaybackFocus(false);
    };
  }, [fromRadio]);
  useEffect(() => {
  if (isPlaying && activeArticle) {
    setHasPlayedOnce(true);
  }
}, [isPlaying, activeArticle]);
  useEffect(() => {
    if (fromRadio) setPlaybackFocus(isFocused);
    if (fromRadio && isFocused) {
      trackInteraction({
        event: ANALYTICS_EVENT.INTERACTION,
        action: ACTION_TYPE.CLICK,
        screen_name: ROUTES.HOME,
        component: "ListenTab",
        context: { flow: "listen", cta: "AUDIO_SESSION_START" },
      });
    }
  }, [isFocused, fromRadio]);

  useEffect(() => {
    const articleId = (route?.params as any)?.articleId;

    if (articleId) {
      setActiveArticleId(articleId);
    }
  }, [(route?.params as any)?.articleId]);

  const {
    articles,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    isLoading,
    isFetched,
    error,
  } = useArticlesData();

  const { rows, articleRowIndexMap } = useTemplateRows(articles);

  /* ---------------- scroll retry ---------------- */

  const handleScrollToIndexFailed = useCallback((info) => {
    clearTimeout(scrollRetryTimeout.current);

    scrollRetryTimeout.current = setTimeout(() => {
      scrollViewRef.current?.scrollToIndex({
        index: info.index,
        animated: true,
        viewPosition: 0.5,
      });
    }, 250);
  }, []);

  /* ---------------- auto scroll ---------------- */

  useEffect(() => {
    if (!activeArticle || !scrollViewRef.current) return;

    if (isUserScrolling.current) return;

    if (lastScrolledArticle.current === activeArticle.id) return;

    const rowIndex = articleRowIndexMap.get(activeArticle.id);
    if (rowIndex == null) return;

    lastScrolledArticle.current = activeArticle.id;

    scrollViewRef.current.scrollToIndex({
      index: rowIndex,
      animated: true,
      viewPosition: 0.5,
    });

  }, [activeArticle?.id, articleRowIndexMap]);

  /* ---------------- refresh ---------------- */

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);

    try {
      await fetchPreviousPage();
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchPreviousPage]);

  /* ---------------- scroll load more ---------------- */

  const handleScroll = useCallback(
    (event) => {
      const { contentOffset, layoutMeasurement, contentSize } =
        event.nativeEvent;

      const scrollY = contentOffset.y;

      if (
        scrollY + layoutMeasurement.height >=
        contentSize.height - LOAD_MORE_THRESHOLD
      ) {
        if (hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  /* ---------------- states ---------------- */

  if (!articles.length && (!isFetched || isLoading)) {
    return <AppLoader />;
  }

  if (error && !articles.length)
    return <Text style={styles.errorText}>Failed to load articles</Text>;

  if (!articles.length && isFetched)
    return <Text style={styles.emptyText}>No articles available</Text>;

  /* ---------------- render ---------------- */

  return (
    <View style={styles.root}>
      <CustomLinearGradient style={{ height: "100%", padding: 0 }}
        gradientType={GRADIENT_TYPE.APP_CARD}>
        <NewsFeedList
          rows={rows}
          scrollViewRef={scrollViewRef}
          handleScroll={handleScroll}
          handleRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          fromRadio={fromRadio}
          isFetchingPreviousPage={isFetchingPreviousPage}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onScrollToIndexFailed={handleScrollToIndexFailed}
          onScrollBeginDrag={() => {
            isUserScrolling.current = true;
          }}
          onMomentumScrollEnd={() => {
            isUserScrolling.current = false;
          }}
        />
      </CustomLinearGradient>

      {fromRadio && hasPlayedOnce && !!activeArticle && <MiniAudioPlayer />}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  errorText: {
    margin: 16,
    color: "#ff6b35",
  },
  emptyText: {
    margin: 16,
  },
});